/**
 * ZielgruppeCardContent Component
 * Displays Zielgruppe (Target Group) data in a card layout
 * Shows personas, age spectrum donut chart, and education bar chart
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import type { ZielgruppeFullData } from '@/types/zielgruppe';
import type { ChartConfig } from '@/types/api';

export interface ZielgruppeCardContentProps {
  data: ZielgruppeFullData;
}

// Color palette for pie chart - using brand gradient colors
const PIE_COLORS = [
  '#FC03B0', // Primary pink (brand start)
  '#FF6803', // Primary orange (brand end)
  '#E8148A', // Darker pink
  '#FF8533', // Lighter orange
  '#D02090', // Deep magenta
  '#FFB366', // Soft orange
  '#A01870', // Muted magenta
  '#CC5200', // Deep orange
];

/**
 * Card content for Zielgruppe displaying personas, age spectrum, and education cluster
 */
export function ZielgruppeCardContent({
  data,
}: ZielgruppeCardContentProps) {
  const { t } = useTranslation();
  const { main, personas } = data;

  // Create pie chart config for Altersspektrum
  const altersspektrumChartConfig = useMemo((): ChartConfig | null => {
    const altersspektrum = main.Zielgruppe_Altersspektrum_array;
    if (!altersspektrum || altersspektrum.length === 0) {
      return null;
    }

    return {
      type: 'pie',
      series: altersspektrum.map(a => a.Zielgruppe_Altersspektrum_item_prozent) as unknown as ChartConfig['series'],
      options: {
        labels: altersspektrum.map(a => a.Zielgruppe_Altersspektrum_item_name),
        colors: PIE_COLORS.slice(0, altersspektrum.length),
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
        stroke: {
          width: 2,
          colors: ['#fff'],
        },
      } as ChartConfig['options'],
    };
  }, [main.Zielgruppe_Altersspektrum_array]);

  // Create bar chart config for Bildungscluster with labels
  const bildungsclusterChartConfig = useMemo((): ChartConfig | null => {
    const bildungscluster = main.Zielgruppe_Bildungscluster_array;
    if (!bildungscluster || bildungscluster.length === 0) {
      return null;
    }

    return {
      type: 'bar',
      series: [{
        name: 'Anteil',
        data: bildungscluster.map(b => b.Zielgruppe_Bildungscluster_item_prozent),
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'bar',
        },
        plotOptions: {
          bar: {
            borderRadius: 2,
            columnWidth: '65%',
          },
        },
        colors: ['#FC03B0'], // Brand pink
        xaxis: {
          categories: bildungscluster.map(b => b.Zielgruppe_Bildungscluster_item_name),
          labels: {
            show: true,
            rotate: -45,
            rotateAlways: true,
            style: {
              fontSize: '9px',
              colors: '#32373c',
            },
            offsetY: 0,
            trim: true,
            maxHeight: 50,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          show: false,
        },
        grid: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Zielgruppe_Bildungscluster_array]);

  return (
    <div className="space-y-4">
      {/* Personas section - Top 3 */}
      <div>
        <h4 className="text-sm font-semibold text-[var(--color-charcoal)] mb-3">{t('results.cards.targetGroup.card.personas')}</h4>
        <div className="flex justify-between gap-2">
          {personas.map((persona, index) => (
            <div key={index} className="flex-1 text-center min-w-0">
              {/* Ranking number */}
              <p className="text-xs font-semibold text-[var(--color-charcoal)]/60">
                #{index + 1}
              </p>
              {/* Percentage */}
              <p className="text-base font-bold text-[var(--color-primary-start)]">
                {persona.prozent}%
              </p>
              {/* Name */}
              <p className="text-[10px] leading-tight font-medium text-[var(--color-charcoal)] line-clamp-2 break-words" title={persona.name}>
                {persona.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Charts section */}
      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[var(--glass-border)]">
        {/* Left: Altersspektrum Pie Chart */}
        <div>
          <h4 className="text-sm font-semibold text-[var(--color-primary-start)] text-center mb-2">
            {t('results.cards.targetGroup.card.ageSpectrum')}
          </h4>
          {altersspektrumChartConfig ? (
            <div className="flex flex-col items-center">
              <AnalysisChart
                config={altersspektrumChartConfig}
                height={140}
              />
              <p className="text-xs text-[var(--color-charcoal)]/70 mt-1">
                {t('results.cards.targetGroup.card.medianAge')} {main.Zielgruppe_Altersspektrum_median}
              </p>
            </div>
          ) : (
            <div className="h-[140px] flex items-center justify-center bg-[var(--glass-bg)] rounded-md">
              <span className="text-xs text-[var(--color-charcoal)]/50">{t('results.cards.targetGroup.card.noData')}</span>
            </div>
          )}
        </div>

        {/* Right: Bildungscluster Bar Chart */}
        <div>
          <h4 className="text-sm font-semibold text-[var(--color-primary-start)] text-center mb-2">
            {t('results.cards.targetGroup.card.educationCluster')}
          </h4>
          {bildungsclusterChartConfig ? (
            <AnalysisChart
              config={bildungsclusterChartConfig}
              height={140}
            />
          ) : (
            <div className="h-[140px] flex items-center justify-center bg-[var(--glass-bg)] rounded-md">
              <span className="text-xs text-[var(--color-charcoal)]/50">{t('results.cards.targetGroup.card.noData')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ZielgruppeCardContent;
